import React, { useState, useRef, useEffect } from 'react';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { Badge } from 'primereact/badge';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';

const BulkDebitRequest = () => {
    // Refs
    const toast = useRef(null);
    const dt = useRef(null);

    // State variables
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [records, setRecords] = useState([]);
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [totalAmount, setTotalAmount] = useState(0);
    const [notes, setNotes] = useState('');
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    
    // Initialize filters with proper structure
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        amortID: { value: null, matchMode: FilterMatchMode.CONTAINS },
        mdid: { value: null, matchMode: FilterMatchMode.CONTAINS },
        loanAccountNo: { value: null, matchMode: FilterMatchMode.CONTAINS },
        groupID: { value: null, matchMode: FilterMatchMode.CONTAINS },
        accountHolderName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        period: { value: null, matchMode: FilterMatchMode.CONTAINS },
        loanType: { value: null, matchMode: FilterMatchMode.CONTAINS },
        status: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });

    // Formatting functions
    const formatDate = (d) => {
        if (!d) return "";
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const formatCurrency = (value) => {
        if (!value) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const formatDateDisplay = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Calculate total amount of selected records
    const calculateTotal = (selectedIds) => {
        const total = records
            .filter(record => selectedIds.includes(record.amortID || record.AmortID))
            .reduce((sum, record) => sum + (parseFloat(record.payment) || 0), 0);
        setTotalAmount(total);
    };

    // Handle global filter change
    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        setGlobalFilterValue(value);
        setFilters(prev => ({
            ...prev,
            global: { value: value, matchMode: FilterMatchMode.CONTAINS }
        }));
    };

    // Clear global filter
    const clearGlobalFilter = () => {
        setGlobalFilterValue('');
        setFilters(prev => ({
            ...prev,
            global: { value: null, matchMode: FilterMatchMode.CONTAINS }
        }));
    };

    // Handle selection change
    const handleSelectionChange = (e) => {
        const selectedIds = e.value.map(record => record.amortID || record.AmortID);
        setSelectedRecords(selectedIds);
        calculateTotal(selectedIds);
    };

    // Handle individual checkbox change
    const handleRecordSelect = (rowData, checked) => {
        const id = rowData.amortID || rowData.AmortID;
        let newSelection;
        if (checked) {
            newSelection = [...selectedRecords, id];
        } else {
            newSelection = selectedRecords.filter(selectedId => selectedId !== id);
        }
        setSelectedRecords(newSelection);
        calculateTotal(newSelection);
    };

    // Handle Select All - selects only visible/filtered records
    const handleSelectAll = () => {
        // Get currently visible records (considering filters)
        let visibleRecords = records;
        if (dt.current) {
            // If DataTable has filters applied, get filtered value
            const filteredValue = dt.current.getFilteredValue ? dt.current.getFilteredValue() : null;
            if (filteredValue && filteredValue.length > 0) {
                visibleRecords = filteredValue;
            }
        }
        
        const allIds = visibleRecords.map(record => record.amortID || record.AmortID);
        const selectableIds = allIds.filter(id => {
            const record = records.find(r => (r.amortID || r.AmortID) === id);
            return record && isSelectable(record);
        });
        
        // Check if all selectable visible records are already selected
        const allSelected = selectableIds.every(id => selectedRecords.includes(id));
        
        if (allSelected) {
            // Deselect all visible records
            const remainingIds = selectedRecords.filter(id => !allIds.includes(id));
            setSelectedRecords(remainingIds);
            calculateTotal(remainingIds);
            toast.current.show({
                severity: 'info',
                summary: 'Deselected',
                detail: `Deselected ${selectableIds.length} records from current view`,
                life: 3000
            });
        } else {
            // Select all visible records
            const newSelection = [...selectedRecords];
            selectableIds.forEach(id => {
                if (!newSelection.includes(id)) {
                    newSelection.push(id);
                }
            });
            setSelectedRecords(newSelection);
            calculateTotal(newSelection);
            toast.current.show({
                severity: 'success',
                summary: 'Selected',
                detail: `Selected ${selectableIds.length} records from current view`,
                life: 3000
            });
        }
    };

    // Clear all selections
    const clearSelections = () => {
        setSelectedRecords([]);
        setTotalAmount(0);
        toast.current.show({
            severity: 'info',
            summary: 'Cleared',
            detail: 'All selections cleared',
            life: 3000
        });
    };

    // Fetch records based on date range
    const fetchRecords = async () => {
        if (!dateFrom || !dateTo) {
            toast.current.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please select both dates',
                life: 3000
            });
            return;
        }

        setLoading(true);
        setRecords([]);
        setSelectedRecords([]);
        setTotalAmount(0);
        setGlobalFilterValue('');
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            amortID: { value: null, matchMode: FilterMatchMode.CONTAINS },
            mdid: { value: null, matchMode: FilterMatchMode.CONTAINS },
            loanAccountNo: { value: null, matchMode: FilterMatchMode.CONTAINS },
            groupID: { value: null, matchMode: FilterMatchMode.CONTAINS },
            accountHolderName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            period: { value: null, matchMode: FilterMatchMode.CONTAINS },
            loanType: { value: null, matchMode: FilterMatchMode.CONTAINS },
            status: { value: null, matchMode: FilterMatchMode.CONTAINS }
        });

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_PATH}bulkdebit/get-debit-records`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        startDate: formatDate(dateFrom),
                        endDate: formatDate(dateTo)
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                // Ensure consistent field naming
                const formattedData = data.data.map(item => ({
                    ...item,
                    amortID: item.amortID || item.AmortID,
                    payDate: item.payDate || item.paydate,
                    loanAccountNo: item.loanAccountNo || item.loanaccountno,
                    accountHolderName: item.accountHolderName || item.accountHolderName,
                    loanType: item.loanType || item.loan_type,
                    groupID: item.groupID || item.groupId || item.groupid || item.GroupID
                }));
                setRecords(formattedData || []);
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: `Found ${formattedData?.length || 0} records`,
                    life: 3000
                });
            } else {
                throw new Error(data.message || 'Failed to fetch records');
            }
        } catch (error) {
            console.error("Error fetching records:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    // Submit selected records
    const handleSubmit = () => {
        if (selectedRecords.length === 0) {
            toast.current.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please select at least one record to submit',
                life: 3000
            });
            return;
        }
        setShowConfirmDialog(true);
    };

    const confirmSubmit = async () => {
        setShowConfirmDialog(false);
        setSubmitting(true);

        try {
            const selectedFullRecords = records.filter(r => 
                selectedRecords.includes(r.amortID || r.AmortID)
            );

            const response = await fetch(
                `${process.env.REACT_APP_API_PATH}bulkdebit/send-request`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        selectedRecords: selectedRecords,
                        selectedFullRecords: selectedFullRecords,
                        startDate: formatDate(dateFrom),
                        endDate: formatDate(dateTo),
                        notes: notes,
                        totalAmount: totalAmount,
                        recordCount: selectedRecords.length
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: `Successfully submitted ${selectedRecords.length} records`,
                    life: 5000
                });
                setNotes('');
                setSelectedRecords([]);
                setTotalAmount(0);
                await fetchRecords();
            } else {
                throw new Error(data.message || 'Submission failed');
            }
        } catch (error) {
            console.error("Error submitting records:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 5000
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Custom templates for DataTable
    const paymentBodyTemplate = (rowData) => {
        return (
            <span className="font-bold text-green-600">
                {formatCurrency(rowData.payment)}
            </span>
        );
    };

    const statusBodyTemplate = (rowData) => {
        const status = rowData.status || 'pending';
        const severityMap = {
            'submitted': 'success',
            'pending': 'warning',
            'failed': 'danger',
            'processing': 'info'
        };
        return <Tag value={status.toUpperCase()} severity={severityMap[status] || 'info'} />;
    };

    const dateBodyTemplate = (rowData) => {
        return formatDateDisplay(rowData.payDate);
    };

    // Check if record can be selected
    const isSelectable = (rowData) => {
        return rowData.status !== 'submitted' && rowData.status !== 'processing';
    };

    // Toolbar components
    const leftToolbarTemplate = () => {
        return (
            <div className="flex flex-wrap align-items-center gap-3">
                <span className="text-xl font-bold">Debit Records</span>
                <Badge value={records.length} severity="info" />
                {selectedRecords.length > 0 && (
                    <>
                        <Badge value={`${selectedRecords.length} selected`} severity="warning" />
                        <span className="text-sm font-semibold text-green-600">
                            Total: {formatCurrency(totalAmount)}
                        </span>
                    </>
                )}
            </div>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <div className="flex flex-wrap gap-2">
                <Button
                    label="Select All"
                    icon="pi pi-check-square"
                    className="p-button-outlined"
                    onClick={handleSelectAll}
                    disabled={records.length === 0}
                />
                <Button
                    label="Clear Selection"
                    icon="pi pi-times-circle"
                    className="p-button-outlined p-button-danger"
                    onClick={clearSelections}
                    disabled={selectedRecords.length === 0}
                />
                <Button
                    label="Refresh"
                    icon="pi pi-refresh"
                    className="p-button-outlined"
                    onClick={fetchRecords}
                    loading={loading}
                />
                <Button
                    label={`Submit (${selectedRecords.length})`}
                    icon="pi pi-check"
                    className="p-button-success"
                    onClick={handleSubmit}
                    loading={submitting}
                    disabled={selectedRecords.length === 0 || submitting}
                />
            </div>
        );
    };

    // Global filter header template
    const globalFilterHeader = () => {
        return (
            <div className="flex align-items-center gap-3 p-3 bg-white border-round">
                <div className="flex align-items-center gap-2 flex-1">
                    <i className="pi pi-search" style={{ fontSize: '1.2rem', color: '#6b7280' }}></i>
                    <InputText
                        value={globalFilterValue}
                        onChange={onGlobalFilterChange}
                        placeholder="Global Search - Filter across all columns"
                        className="w-full"
                        style={{ minWidth: '200px' }}
                    />
                    {globalFilterValue && (
                        <Button
                            icon="pi pi-times"
                            className="p-button-outlined p-button-sm"
                            onClick={clearGlobalFilter}
                            tooltip="Clear global search"
                            type="button"
                        />
                    )}
                </div>
                <div className="flex align-items-center gap-2">
                    <Badge value={`Total: ${records.length}`} severity="info" />
                    {selectedRecords.length > 0 && (
                        <Badge value={`Selected: ${selectedRecords.length}`} severity="success" />
                    )}
                </div>
            </div>
        );
    };

    return (
        <div id="BulkDebitRequest" className="bulk-debit-request">
            <Toast ref={toast} />

            <Card>
                <div className="grid">
                    <div className="col-12">
                        <h1 className="text-2xl font-bold text-primary m-0">Bulk Debit Request</h1>
                        <p className="text-500 mt-2">View, filter, and submit bulk debit requests for loan payments</p>
                    </div>
                </div>

                {/* Date Selection */}
                <div className="grid p-4 bg-blue-50 border-round-lg mt-3">
                    <div className="col-12 md:col-5">
                        <div className="field">
                            <label htmlFor="dateFrom" className="font-semibold block mb-2">
                                <i className="pi pi-calendar mr-2" />
                                Date From
                            </label>
                            <Calendar
                                id="dateFrom"
                                showIcon
                                showButtonBar
                                dateFormat="yy-mm-dd"
                                className="w-full"
                                selectionMode="single"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.value)}
                                placeholder="Select start date"
                            />
                        </div>
                    </div>
                    
                    <div className="col-12 md:col-5">
                        <div className="field">
                            <label htmlFor="dateTo" className="font-semibold block mb-2">
                                <i className="pi pi-calendar mr-2" />
                                Date To
                            </label>
                            <Calendar
                                id="dateTo"
                                showIcon
                                showButtonBar
                                dateFormat="yy-mm-dd"
                                className="w-full"
                                selectionMode="single"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.value)}
                                placeholder="Select end date"
                            />
                        </div>
                    </div>
                    
                    <div className="col-12 md:col-2 flex align-items-end">
                        <Button
                            label="Search"
                            icon="pi pi-search"
                            className="w-full p-button-primary"
                            onClick={fetchRecords}
                            loading={loading}
                            disabled={!dateFrom || !dateTo}
                            style={{ height: '42px' }}
                        />
                    </div>
                </div>

                {/* DataTable Section */}
                <div className="mt-3">
                    <Toolbar
                        className="mb-4"
                        left={leftToolbarTemplate}
                        right={rightToolbarTemplate}
                    />

                    {loading ? (
                        <div className="flex justify-content-center align-items-center p-6">
                            <ProgressSpinner />
                            <span className="ml-3">Loading records...</span>
                        </div>
                    ) : (
                        <DataTable
                            ref={dt}
                            value={records}
                            paginator
                            rows={10}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            className="p-datatable-sm"
                            emptyMessage="No records found. Please search with date range."
                            sortMode="multiple"
                            removableSort
                            responsiveLayout="scroll"
                            breakpoint="960px"
                            selection={records.filter(r => selectedRecords.includes(r.amortID || r.AmortID))}
                            onSelectionChange={handleSelectionChange}
                            dataKey="amortID"
                            selectionMode="multiple"
                            filters={filters}
                            globalFilterFields={[
                                'amortID',
                                'mdid', 
                                'loanAccountNo',
                                'groupID', 
                                'accountHolderName', 
                                'period',
                                'loanType',
                                'status'
                            ]}
                            header={globalFilterHeader}
                            filterDisplay="menu"
                            stripedRows
                            showGridlines
                        >
                            <Column
                                selectionMode="multiple"
                                headerStyle={{ width: '3rem' }}
                                body={(rowData) => {
                                    const disabled = !isSelectable(rowData);
                                    const id = rowData.amortID || rowData.AmortID;
                                    return (
                                        <Checkbox
                                            checked={selectedRecords.includes(id)}
                                            onChange={(e) => handleRecordSelect(rowData, e.checked)}
                                            disabled={disabled}
                                        />
                                    );
                                }}
                            />
                            <Column
                                field="amortID"
                                header="ID"
                                sortable
                                style={{ width: '8%' }}
                            />
                            <Column
                                field="mdid"
                                header="MD ID"
                                sortable
                                style={{ width: '8%' }}
                            />
                            <Column
                                field="loanAccountNo"
                                header="Account No"
                                sortable
                                style={{ width: '12%' }}
                            />
                            <Column
                                field="groupID"
                                header="Group ID"
                                sortable
                                style={{ width: '10%' }}
                            />
                            <Column
                                field="accountHolderName"
                                header="Account Holder"
                                sortable
                                style={{ width: '15%' }}
                            />
                            <Column
                                field="period"
                                header="Period"
                                sortable
                                style={{ width: '8%' }}
                            />
                            <Column
                                field="payDate"
                                header="Pay Date"
                                body={dateBodyTemplate}
                                sortable
                                style={{ width: '10%' }}
                            />
                            <Column
                                field="payment"
                                header="Amount"
                                body={paymentBodyTemplate}
                                sortable
                                style={{ width: '10%' }}
                            />
                            <Column
                                field="loanType"
                                header="Loan Type"
                                sortable
                                style={{ width: '12%' }}
                            />
                            <Column
                                field="status"
                                header="Status"
                                body={statusBodyTemplate}
                                sortable
                                style={{ width: '10%' }}
                            />
                        </DataTable>
                    )}
                </div>

                {/* Summary Footer */}
                {records.length > 0 && !loading && (
                    <div className="grid mt-3">
                        <div className="col-12">
                            <Card className="bg-gray-50">
                                <div className="grid">
                                    <div className="col-12 md:col-3">
                                        <div className="text-center">
                                            <span className="text-sm text-gray-500 block">Total Records</span>
                                            <span className="text-2xl font-bold">{records.length}</span>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <div className="text-center">
                                            <span className="text-sm text-gray-500 block">Filtered View</span>
                                            <span className="text-2xl font-bold text-blue-600">
                                                {dt.current?.getFilteredValue ? dt.current.getFilteredValue().length : records.length}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <div className="text-center">
                                            <span className="text-sm text-gray-500 block">Selected Amount</span>
                                            <span className="text-2xl font-bold text-green-600">
                                                {formatCurrency(totalAmount)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-12 md:col-3">
                                        <div className="text-center">
                                            <span className="text-sm text-gray-500 block">Pending Submission</span>
                                            <span className="text-2xl font-bold text-orange-600">
                                                {records.filter(r => r.status !== 'submitted' && r.status !== 'processing').length}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                )}
            </Card>

            {/* Confirmation Dialog */}
            <Dialog
                header="Confirm Bulk Submission"
                visible={showConfirmDialog}
                style={{ width: '500px' }}
                footer={
                    <div>
                        <Button 
                            label="Cancel" 
                            icon="pi pi-times" 
                            className="p-button-text" 
                            onClick={() => {
                                setShowConfirmDialog(false);
                                setNotes('');
                            }} 
                        />
                        <Button 
                            label="Confirm Submission" 
                            icon="pi pi-check" 
                            className="p-button-success" 
                            onClick={confirmSubmit} 
                            autoFocus 
                        />
                    </div>
                }
                onHide={() => {
                    setShowConfirmDialog(false);
                    setNotes('');
                }}
            >
                <div className="p-3">
                    <div className="flex align-items-center gap-3 mb-4">
                        <i className="pi pi-exclamation-triangle text-4xl text-warning" style={{ color: '#f59e0b' }}></i>
                        <div>
                            <h3 className="m-0">Confirm Submission</h3>
                            <p className="text-500 m-0">You are about to submit {selectedRecords.length} records</p>
                        </div>
                    </div>

                    <div className="bg-gray-50 p-3 border-round mb-3">
                        <div className="flex justify-content-between mb-2">
                            <span className="font-semibold">Total Records:</span>
                            <span>{selectedRecords.length}</span>
                        </div>
                        <div className="flex justify-content-between mb-2">
                            <span className="font-semibold">Total Amount:</span>
                            <span className="font-bold text-green-600">{formatCurrency(totalAmount)}</span>
                        </div>
                        <div className="flex justify-content-between">
                            <span className="font-semibold">Date Range:</span>
                            <span>
                                {dateFrom && dateTo ? 
                                    `${formatDateDisplay(dateFrom)} to ${formatDateDisplay(dateTo)}` : 
                                    'Not specified'
                                }
                            </span>
                        </div>
                    </div>

                    <div className="field">
                        <label className="font-semibold block mb-2">Notes (Optional)</label>
                        <InputTextarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Add notes for this submission..."
                            rows={3}
                            className="w-full"
                        />
                    </div>

                    <Message
                        severity="warn"
                        text="This action will submit all selected records for processing and cannot be undone."
                        className="mt-2"
                    />
                </div>
            </Dialog>
        </div>
    );
};

export default BulkDebitRequest;